import { useState, useEffect, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Plus, List, MessageCircle } from 'lucide-react';
import { supportService } from '../../services/supportService';
import { useCopy, fill } from '../../i18n';
import TicketForm from './TicketForm';
import TicketList from './TicketList';
import TicketConversation from './TicketConversation';
import type { SupportTicket } from '../../types/database';
import './SupportPanel.css';

interface SupportPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type View = 'menu' | 'new' | 'list' | 'conversation';

const SupportPanel = ({ open, onOpenChange }: SupportPanelProps) => {
  const t = useCopy().account.support.panel;
  const [view, setView] = useState<View>('menu');
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [openCount, setOpenCount] = useState(0);

  const loadOpenCount = useCallback(async () => {
    try {
      const count = await supportService.getMyOpenTicketCount();
      setOpenCount(count);
    } catch {
      // silent
    }
  }, []);

  useEffect(() => {
    if (open) {
      loadOpenCount();
    }
  }, [open, loadOpenCount]);

  const handleTicketCreated = () => {
    setView('list');
    loadOpenCount();
  };

  const handleViewTicket = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setView('conversation');
  };

  const handleBack = () => {
    if (view === 'conversation') {
      setView('list');
      setSelectedTicket(null);
    } else if (view === 'new' || view === 'list') {
      setView('menu');
    }
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) {
      onOpenChange(false);
      // Reset view after close animation
      setTimeout(() => {
        setView('menu');
        setSelectedTicket(null);
      }, 200);
    }
  };

  const getTitle = () => {
    switch (view) {
      case 'menu': return t.menuTitle;
      case 'new': return t.newTitle;
      case 'list': return t.listTitle;
      case 'conversation': return fill(t.ticketTitle, { number: selectedTicket?.ticket_number || '' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="support-panel-dialog max-w-[480px] max-h-[85vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-5 py-4 border-b border-[var(--color-border)] flex-shrink-0">
          <div className="flex items-center gap-2">
            {view !== 'menu' && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={handleBack}
              >
                <ArrowLeft size={18} />
              </Button>
            )}
            <DialogTitle className="text-base">{getTitle()}</DialogTitle>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto">
          {view === 'menu' && (
            <div className="p-5 space-y-3">
              <p className="text-sm text-[var(--color-text-secondary)] mb-4">
                {t.menuIntro}
              </p>

              <button
                className="support-menu-item"
                onClick={() => setView('new')}
              >
                <div className="support-menu-icon bg-[rgba(126,62,238,0.1)] text-[var(--color-primary)]">
                  <Plus size={20} />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-sm text-[var(--color-text)]">{t.newItem}</span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {t.newItemHint}
                  </span>
                </div>
              </button>

              <button
                className="support-menu-item"
                onClick={() => setView('list')}
              >
                <div className="support-menu-icon bg-[rgba(59,130,246,0.1)] text-[var(--color-info)]">
                  <List size={20} />
                </div>
                <div className="flex-1">
                  <span className="font-medium text-sm text-[var(--color-text)]">
                    {t.listItem}
                    {openCount > 0 && (
                      <span className="ml-2 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-[var(--color-primary)] text-white text-[11px] font-semibold">
                        {openCount}
                      </span>
                    )}
                  </span>
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    {t.listItemHint}
                  </span>
                </div>
              </button>
            </div>
          )}

          {view === 'new' && (
            <TicketForm
              onCreated={handleTicketCreated}
              onCancel={() => setView('menu')}
            />
          )}

          {view === 'list' && (
            <TicketList
              onViewTicket={handleViewTicket}
              onNewTicket={() => setView('new')}
            />
          )}

          {view === 'conversation' && selectedTicket && (
            <TicketConversation
              ticketId={selectedTicket.id}
              onStatusChange={loadOpenCount}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SupportPanel;
